/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file CallerIdentityBlock.jsx
 * @description
 * Shows WHO the other party of a VoIP call is, on the deep-blue "conversation"
 * surfaces (ringing screen, floating banner, active-call bar):
 *  - CONFIRMED match: name as title, company/detail subtitle, localized type
 *    chip and an expandable "+N more" list when several records match;
 *  - AI SUGGESTION: the NUMBER stays the title; below it a clearly uncertain,
 *    muted block (italic, dashed info-blue border, "AI suggestion · unverified"
 *    badge, ~confidence, reasoning, tappable sources, "search again");
 *  - searching / unknown: a subtle status line.
 * Display logic lives in {@link module:helpers/voipCallerIdentity}.
 *
 * Pure JavaScript (ESM/JSX), no TypeScript. iOS + Android.
 */

import React, {useMemo, useState} from 'react';
import {Linking, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {CONVERSATION} from '../../helpers/managementTheme';
import {SCOPE_I18N, describeCallerIdentity, isSilentUnknown, matchScope, samePhone, suggestionLabel} from '../../helpers/voipCallerIdentity';
import Icon from '../management/SafeIcon';

/** Semantic "info" blue for the unverified AI guess (never the brand orange). */
const INFO = '#60A5FA';
const INFO_BORDER = 'rgba(96,165,250,0.55)';
const INFO_BG = 'rgba(96,165,250,0.10)';
const SURE = '#22C55E';

/**
 * Certainty badge of a match: "Biztos!" (sure) for a record of the current
 * restaurant, dashed "Becslés" (estimate) for a global one found elsewhere.
 * @param {object} props
 * @param {('restaurant'|'global')} props.scope
 * @param {Function} props.t i18next translator.
 * @param {boolean} [props.small] Compact variant for the "+N more" rows.
 * @returns {JSX.Element}
 */
function ScopeBadge({scope, t, small = false}) {
    const [key, fallback] = SCOPE_I18N[scope] || SCOPE_I18N.restaurant;
    const sure = scope !== 'global';

    return (
        <View
            testID={sure ? 'caller-scope-sure' : 'caller-scope-estimate'}
            style={[styles.scope, small && styles.scopeSmall, sure ? styles.scopeSure : styles.scopeEstimate]}
        >
            <Text style={[styles.scopeText, small && styles.scopeTextSmall, sure ? styles.scopeTextSure : styles.scopeTextEstimate]}>
                {t(key, fallback)}
            </Text>
        </View>
    );
}

/**
 * @param {object} props
 * @param {object} props.identity `{contact, matches, suggestion, suggestionStatus}` from {@link module:helpers/CallProvider useVoip}.
 * @param {string} props.number The remote number.
 * @param {boolean} [props.searching] An AI search is in flight.
 * @param {() => void} [props.onRefresh] "Search again" handler (`ai=refresh`).
 * @param {('screen'|'banner')} [props.variant] `screen` = full ringing screen, `banner` = compact one/two-line.
 * @param {string} [props.fallbackName] Name shown when nothing is known (push/native name, confirmed only).
 * @returns {JSX.Element}
 */
export default function CallerIdentityBlock({identity, number, searching = false, onRefresh, variant = 'screen', fallbackName = ''}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const view = useMemo(() => describeCallerIdentity(identity, {number, searching}), [identity, number, searching]);
    const banner = variant === 'banner';
    const typeLabel = (key) => (key ? t(key[0], key[1]) : '');
    const openUrl = (url) => {
        Linking.openURL(url).catch(() => {});
    };

    // The dialled number plus the number stored on the record when it is a different line.
    const phones = view.kind === 'confirmed'
        ? [number, view.phone].filter((value, index, all) => value
            && all.findIndex((other) => other && samePhone(other, value)) === index)
        : [];

    const title = view.kind === 'confirmed'
        ? view.title
        : (fallbackName || view.title || t('voip_unknown_caller', 'Ismeretlen hívó'));

    // ---- compact banner -------------------------------------------------
    if (banner) {
        let line = '';
        if (view.kind === 'confirmed') {
            line = [t(...SCOPE_I18N[view.scope]), typeLabel(view.typeKey), view.subtitle, phones.join(' · ')].filter(Boolean).join(' · ');
        } else if (view.kind === 'suggested') {
            line = t('voip_caller_possibly', 'Valószínűleg: {{name}}', {name: suggestionLabel(view.suggestion)});
        } else if (view.kind === 'searching') {
            line = t('voip_caller_searching', 'Keressük, ki lehet…');
        } else if (isSilentUnknown(identity)) {
            line = t('voip_caller_unknown_number', 'Ismeretlen szám');
        }

        return (
            <View style={styles.bannerWrap}>
                <Text style={styles.bannerName} numberOfLines={1}>{title}</Text>
                {!!line && (
                    <Text
                        style={[styles.bannerLine, view.kind === 'suggested' && styles.italicInfo]}
                        numberOfLines={1}
                    >
                        {view.kind === 'suggested' ? '? ' : ''}{line}
                    </Text>
                )}
            </View>
        );
    }

    // ---- full screen ----------------------------------------------------
    return (
        <View style={styles.wrap}>
            <Text style={styles.name} numberOfLines={2}>{title}</Text>
            {view.kind === 'confirmed' && !!view.subtitle && (
                <Text style={styles.subtitle} numberOfLines={2}>{view.subtitle}</Text>
            )}
            {phones.map((phone) => <Text key={phone} style={styles.number}>{phone}</Text>)}
            {view.kind !== 'confirmed' && !!number && <Text style={styles.number}>{number}</Text>}

            {view.kind === 'confirmed' && (
                <>
                    <View style={styles.chipRow}>
                        {!!view.typeKey && (
                            <View style={styles.chip}>
                                <Text style={styles.chipText}>{typeLabel(view.typeKey)}</Text>
                            </View>
                        )}
                        <ScopeBadge scope={view.scope} t={t} />
                    </View>
                    {view.extra.length > 0 && (
                        <>
                            <TouchableOpacity onPress={() => setExpanded((v) => !v)} hitSlop={8} accessibilityRole="button">
                                <Text style={styles.more}>
                                    {expanded
                                        ? t('voip_caller_less', 'Kevesebb')
                                        : t('voip_caller_more', '+{{count}} további', {count: view.extra.length})}
                                </Text>
                            </TouchableOpacity>
                            {expanded && view.extra.map((m, i) => (
                                <View key={`${m.type}-${m.id || i}`} style={styles.extraRow}>
                                    <View style={styles.extraMain}>
                                        <Text style={styles.extraName} numberOfLines={1}>{m.name || number}</Text>
                                        {!!m.phone && <Text style={styles.extraPhone} numberOfLines={1}>{m.phone}</Text>}
                                    </View>
                                    <ScopeBadge scope={matchScope(m)} t={t} small />
                                    <Text style={styles.extraType}>{typeLabel(m.type ? [`voip_caller_type_${m.type}`, m.type] : null)}</Text>
                                </View>
                            ))}
                        </>
                    )}
                </>
            )}

            {view.kind === 'suggested' && (
                <View style={styles.guess}>
                    <Text style={styles.guessTitle}>
                        {t('voip_caller_possibly', 'Valószínűleg: {{name}}', {name: suggestionLabel(view.suggestion)})}
                    </Text>
                    <View style={styles.badgeRow}>
                        <View style={styles.badge}>
                            <Icon name="robot-outline" size={12} color={INFO} />
                            <Text style={styles.badgeText}>{t('voip_caller_ai_badge', 'AI-javaslat · nem ellenőrzött')}</Text>
                        </View>
                        {view.confidencePct != null && <Text style={styles.pct}>~{view.confidencePct}%</Text>}
                    </View>
                    {!!view.suggestion?.reasoning && (
                        <Text style={styles.reasoning} numberOfLines={4}>{view.suggestion.reasoning}</Text>
                    )}
                    {view.sources.slice(0, 3).map((s) => (
                        <TouchableOpacity key={s.url} onPress={() => openUrl(s.url)} hitSlop={6} accessibilityRole="link">
                            <Text style={styles.source} numberOfLines={1}>{s.title || s.url}</Text>
                        </TouchableOpacity>
                    ))}
                    {!!onRefresh && (
                        <TouchableOpacity onPress={onRefresh} disabled={searching} hitSlop={8} accessibilityRole="button">
                            <Text style={[styles.again, searching && {opacity: 0.5}]}>
                                {searching ? t('voip_caller_searching', 'Keressük, ki lehet…') : t('voip_caller_search_again', 'Keresés újra')}
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}

            {view.kind === 'searching' && (
                <Text style={styles.status}>{t('voip_caller_searching', 'Keressük, ki lehet…')}</Text>
            )}
            {view.kind === 'unknown' && isSilentUnknown(identity) && (
                <Text style={styles.status}>{t('voip_caller_unknown_number', 'Ismeretlen szám')}</Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {alignItems: 'center', width: '100%'},
    name: {color: CONVERSATION.text, fontSize: 32, fontWeight: '800', letterSpacing: -0.8, textAlign: 'center', marginTop: 26},
    subtitle: {color: CONVERSATION.subText, fontSize: 16, fontWeight: '600', textAlign: 'center', marginTop: 6},
    number: {color: CONVERSATION.subText, fontSize: 18, fontWeight: '500', marginTop: 8},
    chipRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12},
    scope: {paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999, borderWidth: 1},
    scopeSmall: {paddingHorizontal: 7, paddingVertical: 2},
    scopeSure: {backgroundColor: SURE, borderColor: SURE},
    scopeEstimate: {backgroundColor: INFO_BG, borderColor: INFO_BORDER, borderStyle: 'dashed'},
    scopeText: {fontSize: 12.5, fontWeight: '800'},
    scopeTextSmall: {fontSize: 10.5},
    scopeTextSure: {color: '#FFFFFF'},
    scopeTextEstimate: {color: INFO},
    extraMain: {flex: 1, minWidth: 0, paddingRight: 8},
    extraPhone: {color: CONVERSATION.subText, fontSize: 12, fontWeight: '500', marginTop: 1},
    chip: {paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999, backgroundColor: CONVERSATION.surfaceStrong, borderWidth: 1, borderColor: CONVERSATION.border},
    chipText: {color: '#FFFFFF', fontSize: 12.5, fontWeight: '700'},
    more: {color: INFO, fontSize: 13.5, fontWeight: '700', marginTop: 10},
    extraRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: 320, marginTop: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: CONVERSATION.surfaceStrong},
    extraName: {color: '#FFFFFF', fontSize: 14, fontWeight: '600'},
    extraType: {color: CONVERSATION.subText, fontSize: 12.5, fontWeight: '600'},
    guess: {marginTop: 14, width: '100%', maxWidth: 340, padding: 12, borderRadius: 16, borderWidth: 1.5, borderStyle: 'dashed', borderColor: INFO_BORDER, backgroundColor: INFO_BG},
    guessTitle: {color: CONVERSATION.subText, fontSize: 15, fontWeight: '600', fontStyle: 'italic', textAlign: 'center'},
    badgeRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8},
    badge: {flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, borderWidth: 1, borderColor: INFO_BORDER},
    badgeText: {color: INFO, fontSize: 11.5, fontWeight: '700'},
    pct: {color: INFO, fontSize: 12.5, fontWeight: '700', fontStyle: 'italic'},
    reasoning: {color: CONVERSATION.subText, fontSize: 13, fontStyle: 'italic', textAlign: 'center', marginTop: 8},
    source: {color: INFO, fontSize: 12.5, fontWeight: '600', textDecorationLine: 'underline', textAlign: 'center', marginTop: 6},
    again: {color: INFO, fontSize: 13, fontWeight: '800', textAlign: 'center', marginTop: 10},
    status: {color: CONVERSATION.subText, fontSize: 13.5, fontStyle: 'italic', marginTop: 12, opacity: 0.85},
    bannerWrap: {flex: 1, minWidth: 0},
    bannerName: {fontSize: 16, fontWeight: '800', letterSpacing: -0.2, color: CONVERSATION.text},
    bannerLine: {fontSize: 12.5, fontWeight: '600', marginTop: 1, color: CONVERSATION.subText},
    italicInfo: {fontStyle: 'italic', color: INFO},
});
